import type { EventBus } from "./eventBus.js";
import { getMaxAttempts } from "./retryManager.js";
import type { AddQueueJobOptions, QueueOptions, QueueStats } from "../types/queue.js";
import type { JobRecord, JobTask } from "../types/job.js";
import type { RetryConfig } from "../types/retry.js";
import type { StorageAdapter } from "../types/storage.js";
import { createJobId } from "../utils/id.js";
import { assertSerializablePayload } from "../utils/safeSerialize.js";
import { nowIso } from "../utils/time.js";
import { assertPositiveNumber, assertTask, validateJobName } from "../utils/validation.js";

type QueueRuntime = {
  name: string;
  options: Required<Pick<QueueOptions, "concurrency" | "maxSize">> & QueueOptions;
  pending: string[];
  running: number;
  paused: boolean;
  rateWindowStartedAt: number;
  rateCount: number;
  jobs: Map<string, QueueJobRuntime>;
};

type QueueJobRuntime = {
  task?: JobTask;
  retry?: RetryConfig;
  timeout?: number;
};

export class QueueManager {
  private readonly queues = new Map<string, QueueRuntime>();

  constructor(
    private readonly storage: StorageAdapter,
    private readonly eventBus: EventBus,
    private readonly maxPayloadBytes: number | undefined,
    private readonly runJob: (jobId: string, task?: JobTask, queueName?: string, retry?: RetryConfig, timeout?: number) => Promise<unknown>
  ) {}

  createQueue(name: string, options: QueueOptions = {}): void {
    validateJobName(name);
    const concurrency = options.concurrency ?? 1;
    assertPositiveNumber(concurrency, "queue concurrency");
    const runtime: QueueRuntime = {
      name,
      options: {
        ...options,
        concurrency,
        maxSize: options.maxSize ?? 1000,
      },
      pending: [],
      running: 0,
      paused: options.paused ?? false,
      rateWindowStartedAt: Date.now(),
      rateCount: 0,
      jobs: new Map(),
    };
    this.queues.set(name, runtime);
    this.eventBus.emit("queue:created", { queueName: name, concurrency });
  }

  async addJob<TPayload, TResult>(queueName: string, job: AddQueueJobOptions<TPayload, TResult>): Promise<string> {
    const queue = this.getQueue(queueName);
    validateJobName(job.name);
    assertSerializablePayload(job.payload, this.maxPayloadBytes);
    if (!job.task && !queue.options.processor) {
      throw new Error(`Queue "${queueName}" requires either a queue processor or a task on addJob`);
    }
    if (queue.pending.length >= queue.options.maxSize) {
      throw new Error(`Queue "${queueName}" is full`);
    }

    const id = job.id ?? createJobId("queue");
    const createdAt = nowIso();
    const record: JobRecord<TPayload> = {
      id,
      name: job.name,
      kind: "queue",
      status: "pending",
      payload: job.payload,
      priority: job.priority ?? 0,
      attempts: 0,
      maxAttempts: getMaxAttempts(job.retry ?? queue.options.retry),
      createdAt,
      updatedAt: createdAt,
      successCount: 0,
      failureCount: 0,
      queueName,
    };
    await this.storage.saveJob(record);
    queue.jobs.set(id, {
      task: (job.task ?? queue.options.processor) as JobTask,
      retry: job.retry ?? queue.options.retry,
      timeout: job.timeout ?? queue.options.timeout,
    });
    await this.insertByPriority(queue, id, record.priority);
    void this.drain(queueName, (job.task ?? queue.options.processor) as JobTask);
    return id;
  }

  getQueuedCount(): number {
    return [...this.queues.values()].reduce((total, queue) => total + queue.pending.length, 0);
  }

  getQueueStats(name?: string): QueueStats[] {
    const queues = name ? [this.getQueue(name)] : [...this.queues.values()];
    return queues.map((queue) => {
      const rateLimit = queue.options.rateLimit;
      const now = Date.now();
      return {
        name: queue.name,
        pending: queue.pending.length,
        running: queue.running,
        concurrency: queue.options.concurrency,
        maxSize: queue.options.maxSize,
        paused: queue.paused,
        rateLimit: rateLimit
          ? {
              limit: rateLimit.limit,
              intervalMs: rateLimit.intervalMs,
              used: queue.rateCount,
              resetsInMs: Math.max(0, rateLimit.intervalMs - (now - queue.rateWindowStartedAt)),
            }
          : undefined,
      };
    });
  }

  pauseQueue(name: string): void {
    const queue = this.getQueue(name);
    queue.paused = true;
    this.eventBus.emit("queue:paused", { queueName: name });
  }

  resumeQueue(name: string): void {
    const queue = this.getQueue(name);
    queue.paused = false;
    this.eventBus.emit("queue:resumed", { queueName: name });
    void this.drain(name);
  }

  async clearQueue(name: string): Promise<number> {
    const queue = this.getQueue(name);
    const removedIds = queue.pending.splice(0);
    for (const jobId of removedIds) {
      queue.jobs.delete(jobId);
      await this.storage.updateJob(jobId, { status: "cancelled", finishedAt: nowIso() });
    }
    this.eventBus.emit("queue:cleared", { queueName: name, removed: removedIds.length });
    return removedIds.length;
  }

  async drain(queueName: string, task?: JobTask): Promise<void> {
    const queue = this.getQueue(queueName);
    if (queue.paused) return;
    const effectiveTask = (task ?? queue.options.processor) as JobTask | undefined;
    while (queue.running < queue.options.concurrency && queue.pending.length > 0 && this.canRunByRateLimit(queue)) {
      const jobId = queue.pending.shift();
      if (!jobId) break;
      const runtime = queue.jobs.get(jobId);
      const jobTask = runtime?.task ?? effectiveTask;
      queue.running += 1;
      void this.runJob(jobId, jobTask, queueName, runtime?.retry, runtime?.timeout).finally(() => {
        queue.jobs.delete(jobId);
        queue.running = Math.max(0, queue.running - 1);
        if (queue.pending.length === 0 && queue.running === 0) this.eventBus.emit("queue:drain", { queueName });
        void this.drain(queueName, effectiveTask);
      });
    }
  }

  private getQueue(name: string): QueueRuntime {
    const queue = this.queues.get(name);
    if (!queue) throw new Error(`Queue not found: ${name}`);
    return queue;
  }

  private async insertByPriority(queue: QueueRuntime, jobId: string, priority: number): Promise<void> {
    let index = queue.pending.length;
    for (let i = 0; i < queue.pending.length; i += 1) {
      const existing = await this.storage.getJob(queue.pending[i] as string);
      if ((existing?.priority ?? 0) < priority) {
        index = i;
        break;
      }
    }
    queue.pending.splice(index, 0, jobId);
  }

  private canRunByRateLimit(queue: QueueRuntime): boolean {
    const rateLimit = queue.options.rateLimit;
    if (!rateLimit) return true;
    const now = Date.now();
    if (now - queue.rateWindowStartedAt >= rateLimit.intervalMs) {
      queue.rateWindowStartedAt = now;
      queue.rateCount = 0;
    }
    if (queue.rateCount >= rateLimit.limit) {
      const waitMs = rateLimit.intervalMs - (now - queue.rateWindowStartedAt);
      setTimeout(() => void this.drain(queue.name), Math.max(1, waitMs)).unref?.();
      return false;
    }
    queue.rateCount += 1;
    return true;
  }
}
